// @flow
import {all, takeEvery, put, call, select} from 'redux-saga/effects';
import {Alert, Platform} from 'react-native';
import {stopSubmit} from 'redux-form';
import I18n from '../../../lib/i18n';
import Toast from '../../../lib/toastService';
import type {
  ReferralBonusActionType,
  VerifyBonusesActionType,
  PromotionCodeActionType,
} from './actions';
import {
  setGodfatherExternalId,
  setGodfatherValidity,
  verifyGodfather,
  setActivationBonusValidity,
  setPromotion,
  setPromotionCodeModalVisible,
  setActivationNotFirstSubscription,
} from './actions';
import {
  godfatherExternalIdSelector,
  shouldBenefitFromActivationBonusSelector,
  shouldShowGodfatherSelector,
  promotionSelector,
} from './reducer';
import {setLoading} from '../../LoadingStatus';
import {tripica} from '../../../api';
import config from '../../../config';
import HandleErrorService from '../../../lib/handleError';
import {
  fetchReferralBonuses,
  fetchActivationBonus,
  activationBonusAmountSelector,
} from '../../Bonuses';
import {
  fetchReferralBonusesSaga,
  fetchActivationBonusSaga,
} from '../../Bonuses/sagas';
import {orderUuidSelector} from '../Order';
import {getDeviceUniqueId} from '../../../lib/device';
import {bonusErrorsAndSpecificTranslations} from './constants';
import WizzeeLog from '../../../lib/wizzeeLog';

export function* verifyGodfatherAndGetResponse(
  godfatherExternalId: String,
): Generator<*, *, *> {
  const deviceUniqueId = yield call(getDeviceUniqueId);
  const response = yield call(
    [tripica, 'validateGodfather'],
    godfatherExternalId,
    deviceUniqueId,
  );

  if (!response || !response.errorCodeList) {
    throw new Error('no response or error code list');
  }

  return response;
}

export function* verifyGodfatherSaga(
  action: ReferralBonusActionType,
): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_VERIFY_GODFATHER') return;
  try {
    yield put(setLoading('verifyGodfather', true));

    const response = yield call(
      verifyGodfatherAndGetResponse,
      action.payload.godfatherExternalId,
    );

    yield put(setGodfatherExternalId(action.payload.godfatherExternalId));
    if (yield call(handleGodfatherValidityErrors, response.errorCodeList)) {
      yield put(setGodfatherValidity(true));
      yield put(fetchReferralBonuses());
    }
  } catch (e) {
    WizzeeLog.warn('verifyGodfatherSaga', e);
    HandleErrorService.showToastError(e);
  } finally {
    yield put(setLoading('verifyGodfather', false));
  }
}

export const getGodfatherError = (errorCodeList: string[]): ?string =>
  errorCodeList.find(
    error =>
      ['SUBSCRIBER_AS_REFERRER', 'NOT_FIRST_SUBSCRIPTION'].includes(error) ||
      error.includes('GODFATHER'),
  );

export function* handleGodfatherValidityErrors(
  errorCodeList: string[],
): Generator<*, *, *> {
  const godfatherError = getGodfatherError(errorCodeList);
  if (!godfatherError) {
    return true;
  }

  const specificTranslation =
    bonusErrorsAndSpecificTranslations[godfatherError];

  if (godfatherError.includes('GODFATHER') && !specificTranslation) {
    Toast.showError(I18n.t('SignUp.FriendInvitation.invalid'));
    yield put(setGodfatherValidity(false));
  } else {
    yield put(setGodfatherValidity(false, I18n.t(specificTranslation)));

    if (godfatherError === 'GODFATHER_GODCHILD_NRIC') {
      yield put(setActivationBonusValidity(false));
    }
  }

  return false;
}

function* checkActivationBonusSaga(): Generator<*, *, *> {
  const fetchActivationBonusResult = yield call(
    fetchActivationBonusSaga,
    fetchActivationBonus(),
  );
  yield put(setActivationNotFirstSubscription(false));
  if (!fetchActivationBonusResult.error) {
    try {
      const activationBonusAmount = yield select(activationBonusAmountSelector);
      if (activationBonusAmount) {
        const uuid = yield select(orderUuidSelector);
        const deviceUniqueId = yield call(getDeviceUniqueId);
        const response = yield call(
          [tripica, 'validateActivationBonus'],
          deviceUniqueId,
          uuid,
        );
        const isActivationBonusValid =
          response &&
          response.errorCodeList &&
          !response.errorCodeList.some(error =>
            error.includes('NOT_FIRST_SUBSCRIPTION'),
          );
        yield put(setActivationBonusValidity(isActivationBonusValid));
      }
    } catch (e) {
      WizzeeLog.warn('verifyGodfatherSaga', e);
      HandleErrorService.showToastError(e);
    }
  }
}

export function* verifyBonusesSaga(
  action: VerifyBonusesActionType,
): Generator<*, *, *> {
  if (action.type !== 'VERIFY_BONUSES') return; // for flow
  yield put(setLoading('verifyBonuses', true));
  const godfatherExternalId = yield select(godfatherExternalIdSelector);
  if (!!godfatherExternalId) {
    const fetchReferralBonusesResult = yield call(
      fetchReferralBonusesSaga,
      fetchReferralBonuses(),
    );
    if (!fetchReferralBonusesResult.error) {
      yield call(verifyGodfatherSaga, verifyGodfather(godfatherExternalId));
    }
  }

  if (config.canCustomersHaveActivationBonuses) {
    yield call(checkActivationBonusSaga);
  }

  yield put(setLoading('verifyBonuses', false));
}

export function* verifyPromotionCodeSaga(
  action: PromotionCodeActionType,
): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_VERIFY_PROMOTION_CODE') return; // for flow
  yield put(setLoading('verifyPromotionCode', true));

  const isReferral = new RegExp(config.EXTERNAL_ID_REGEX).test(
    action.payload.promotionCode,
  );
  const defaultInvalidTranslation = isReferral
    ? 'SignUp.FriendInvitation.invalid'
    : 'error.validation.promotionCodeInvalid';

  try {
    if (isReferral) {
      const referralBonuses = yield call([tripica, 'getReferralBonuses']);
      if (!referralBonuses) {
        throw new Error('GODFATHER_EXPIRED');
      }
      yield* verifyAndSetReferralCode(action.payload.promotionCode);
    } else if (config.canEnterPromotionCode) {
      yield* verifyAndSetPromotionCode(action.payload.promotionCode);
    }
  } catch (error) {
    const errorTranslation =
      bonusErrorsAndSpecificTranslations[error?.message] ||
      defaultInvalidTranslation;
    yield put(
      stopSubmit('PromotionCodePage', {code: I18n.t(errorTranslation)}),
    );
  }

  yield put(setLoading('verifyPromotionCode', false));
}

export const verifyReferralCodeAlert = (
  alertType: 'activation' | 'referral' | 'promoCode',
): Promise<boolean> => {
  if (Platform.OS === 'web') {
    return new Promise((resolve, reject) => {
      const result = confirm(
        alertType === 'activation'
          ? I18n.t(
              'SignUp.ConfirmPlan.promotionCode.confirmReferralOverideActivationTitle',
            )
          : alertType === 'referral'
          ? I18n.t(
              'SignUp.ConfirmPlan.promotionCode.confirmReferralOverideReferralTitle',
            )
          : I18n.t(
              'SignUp.ConfirmPlan.promotionCode.confirmReferralOveridePromotionCodeTitle',
            ),
      );
      resolve(result);
    });
  }

  return new Promise((resolve, reject) => {
    Alert.alert(
      alertType === 'activation'
        ? I18n.t(
            'SignUp.ConfirmPlan.promotionCode.alertReferralOverideActivationTitle',
          )
        : alertType === 'referral'
        ? I18n.t(
            'SignUp.ConfirmPlan.promotionCode.alertReferralOverideReferralTitle',
          )
        : I18n.t(
            'SignUp.ConfirmPlan.promotionCode.alertReferralOveridePromotionCodeTitle',
          ),
      I18n.t('SignUp.ConfirmPlan.promotionCode.alertSubtitle'),
      [
        {
          text: I18n.t('SignUp.ConfirmPlan.promotionCode.confirm'),
          onPress: () => resolve(true),
        },
        {
          text: I18n.t('SignUp.ConfirmPlan.promotionCode.cancel'),
          onPress: () => resolve(false),
          style: 'cancel',
        },
      ],
      {cancelable: true},
    );
  });
};
export const verifyPromotionCodeAlert = (
  alertType: 'activation' | 'referral' | 'promoCode',
): Promise<boolean> => {
  if (Platform.OS === 'web') {
    return new Promise((resolve, reject) => {
      const result = confirm(
        alertType === 'activation'
          ? I18n.t(
              'SignUp.ConfirmPlan.promotionCode.confirmPromotionCodeOverideActivationTitle',
            )
          : alertType === 'referral'
          ? I18n.t(
              'SignUp.ConfirmPlan.promotionCode.confirmPromotionCodeOverideReferralTitle',
            )
          : I18n.t(
              'SignUp.ConfirmPlan.promotionCode.confirmPromotionCodeOveridePromotionCodeTitle',
            ),
      );
      resolve(result);
    });
  }
  return new Promise((resolve, reject) => {
    Alert.alert(
      alertType === 'activation'
        ? I18n.t('SignUp.ConfirmPlan.promotionCode.alertActivationTitle')
        : alertType === 'referral'
        ? I18n.t('SignUp.ConfirmPlan.promotionCode.alertReferralTitle')
        : I18n.t('SignUp.ConfirmPlan.promotionCode.alertPromotionCodeTitle'),
      I18n.t('SignUp.ConfirmPlan.promotionCode.alertSubtitle'),
      [
        {
          text: I18n.t('SignUp.ConfirmPlan.promotionCode.confirm'),
          onPress: () => resolve(true),
        },
        {
          text: I18n.t('SignUp.ConfirmPlan.promotionCode.cancel'),
          onPress: () => resolve(false),
          style: 'cancel',
        },
      ],
      {cancelable: true},
    );
  });
};

function* verifyAndSetReferralCode(referralCode: string) {
  const response = yield call(verifyGodfatherAndGetResponse, referralCode);
  const godfatherError = getGodfatherError(response.errorCodeList);
  if (godfatherError) {
    throw new Error(godfatherError);
  }

  const shouldShowGodfather = yield select(shouldShowGodfatherSelector);
  const shouldBenefitFromActivationBonus = yield select(
    shouldBenefitFromActivationBonusSelector,
  );
  const hasPromotionCode = yield select(promotionSelector);
  let hasConfirmed = true;
  if (shouldShowGodfather) {
    hasConfirmed = yield call(verifyReferralCodeAlert, 'referral');
  } else if (
    config.arePromotionCodeAndActivationBonusIncompatible &&
    shouldBenefitFromActivationBonus
  ) {
    hasConfirmed = yield call(verifyReferralCodeAlert, 'activation');
  } else if (hasPromotionCode) {
    hasConfirmed = yield call(verifyReferralCodeAlert, 'promoCode');
  }
  if (hasConfirmed) {
    yield put(setPromotionCodeModalVisible(false));
    yield put(setGodfatherExternalId(referralCode));
    yield put(setGodfatherValidity(true));
    yield put(fetchReferralBonuses());
    yield put(setPromotion(null));
  }
}

function* verifyAndSetPromotionCode(promotionCode: string): Generator<*, *, *> {
  const response = yield call([tripica, 'getPromoCode'], promotionCode);
  const shouldShowGodfather = yield select(shouldShowGodfatherSelector);
  const shouldBenefitFromActivationBonus = yield select(
    shouldBenefitFromActivationBonusSelector,
  );
  const hasPromotionCode = yield select(promotionSelector);
  let hasConfirmed = true;
  if (
    config.arePromotionCodeAndReferralPromotionIncompatible &&
    shouldShowGodfather
  ) {
    hasConfirmed = yield call(verifyPromotionCodeAlert, 'referral');
  } else if (
    config.arePromotionCodeAndActivationBonusIncompatible &&
    shouldBenefitFromActivationBonus
  ) {
    hasConfirmed = yield call(verifyPromotionCodeAlert, 'activation');
  } else if (hasPromotionCode) {
    hasConfirmed = yield call(verifyPromotionCodeAlert, 'promoCode');
  }
  if (hasConfirmed) {
    yield put(setPromotionCodeModalVisible(false));
    yield put(setPromotion(response));
    yield put(setGodfatherExternalId(null));
  }
}

export default function* (): Generator<*, *, *> {
  yield all([
    takeEvery('SIGNUP_VERIFY_GODFATHER', verifyGodfatherSaga),
    takeEvery('SIGNUP_VERIFY_PROMOTION_CODE', verifyPromotionCodeSaga),
    takeEvery('VERIFY_BONUSES', verifyBonusesSaga),
  ]);
}
